/*
Contents of this file was produced with a help of a code generation tool and subsequently reviewed and edited by a human.

While some of the code was created by an AI assisted service, manual adjustments have been made to ensure correctness, readability, functionality and compliance to Avalara's coding standards and guidelines.
Any future modifications should be approached with caution to avoid overwriting the manual changes.

This source code is owned by Avalara Inc. Unauthorized copying of this file, via any medium, is strictly prohibited. Proprietary and confidential.

For more information, refer to the documentation or contact the development team.
*/

import { Component, Element, Event, EventEmitter, h, Host, Prop, State, Watch } from '@stencil/core';
import { createPopper, Instance } from '@popperjs/core';
import { IntlShape } from '@formatjs/intl';
import Cookies from 'js-cookie';
import shadowRootParentOrDocument from '../../utils/generic-functions';
import getCompanyCookieName from '../../utils/company-cookie-name';
import { state } from '../../store';
import { CompanyItem, SCompanyChangeEvent, SearchCompaniesEvent, LoadMoreEvent } from '../../types/types';
import { getDefaultSharedCookieOptions } from '../../utils/cookie';
import getAvaAppUrl from '../../utils/avaapp-urls';

@Component({
  tag: 'aui-company-switcher',
  styleUrl: 'aui-company-switcher.scss',
  shadow: false,
})
export class AuiCompanySwitcher {
  @Prop() companies: CompanyItem[] = [];

  @Prop({ mutable: true }) company: CompanyItem = null;

  @Element() element: HTMLElement;

  @Prop() expanded: boolean = true;

  @Prop({ attribute: 'correlationid' }) correlationId?: string;

  @Prop({ attribute: 'loading' }) loading: boolean = false;

  @Prop({ attribute: 'initialcompanyfetchinprogress' }) initialCompanyFetchInProgress: boolean = false;

  @Prop({ attribute: 'hasmore' }) hasMore: boolean = false;

  @Prop({ attribute: 'mayexpand' }) mayExpand: boolean = false;

  @Prop() companyLogoFileUrl: string | null = null;

  @Watch('expanded')
  handleExpandedChange() {
    this.showFlyout = false;
    this.#popperInstanceRef?.destroy();
    this.#sPopperRef?.classList.add('hidden');
    this.#createFlyout();
  }

  @State() showFlyout: boolean = false;

  @State() searchText: string = '';

  @State() searchDebounceTimer: number | null = null;

  @State() intl: IntlShape;

  @Event({
    eventName: 's-change-company',
    bubbles: true,
    composed: true,
  })
  sChangeCompany: EventEmitter<SCompanyChangeEvent>;

  @Event({
    eventName: 's-search-companies',
    bubbles: true,
    composed: true,
  })
  searchCompanies: EventEmitter<SearchCompaniesEvent>;

  @Event({
    eventName: 's-load-more',
    bubbles: true,
    composed: true,
  })
  loadMore: EventEmitter<LoadMoreEvent>;

  #popperInstanceRef: Instance | null = null;

  #sPopperRef: HTMLSPopperElement | null;

  #handleSelectClear = () => {
    this.searchText = '';

    // Clear existing timer
    if (this.searchDebounceTimer !== null) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }

    // Reset the visible companies to the full, un-filtered list
    this.searchCompanies.emit({ searchTerm: '' });

    const input = this.element.querySelector('s-input-extended input') as HTMLElement;
    if (input) {
      input.focus();
    }
  };

  #handleSelectInput = (e: CustomEvent<{ value: string }>) => {
    this.searchText = e.detail.value;

    // Keep flyout open immediately when typing
    this.showFlyout = true;

    // Clear existing timer
    if (this.searchDebounceTimer !== null) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }

    // Set new timer to debounce search
    this.searchDebounceTimer = window.setTimeout(() => {
      this.searchCompanies.emit({ searchTerm: this.searchText });
      this.searchDebounceTimer = null;
    }, 200);
  };

  #handleLoadMore = () => {
    this.loadMore.emit({ loaded: true });
  };

  /**
   * Either render the company logo or the first letter of the company name
   * @returns JSX.Element
   */
  renderCompanyIcon = () => {
    if (this.companyLogoFileUrl) {
      return (
        <div class="company-icon">
          <img src={this.companyLogoFileUrl} alt={this.textLocalized.companyLogo} class="company-logo" />
        </div>
      );
    }

    // Get the first letter of the company name for the icon
    const initialLetter = this.company?.name.charAt(0).toUpperCase() || '';

    return <div class="company-icon">{initialLetter}</div>;
  };

  /**
   * Renders a status tag if the company is inactive or in setup.
   * @param company - The company to render the tag for.
   * @returns A s-tag component if the company is inactive or in setup, otherwise null.
   */
  #renderStatusTag = (company: CompanyItem) => {
    if (company.isActive === false) {
      return (
        <s-tag color="gray-lighter">
          <span slot="content">{this.textLocalized.inactive}</span>
        </s-tag>
      );
    }
    if (company.inProgress === true) {
      return (
        <s-tag color="gray-lighter">
          <span slot="content">{this.textLocalized.inSetup}</span>
        </s-tag>
      );
    }

    return null;
  };

  #handleCompanyClick = (comp: CompanyItem) => {
    Cookies.set(getCompanyCookieName(window.aui.env), comp.id.toString(), getDefaultSharedCookieOptions());

    this.sChangeCompany.emit({ company: comp, initialLoad: false });
    this.company = comp;
    this.showFlyout = false;
    this.searchText = '';
  };

  get textLocalized() {
    const { intl } = this;
    return {
      switchCompany: intl.formatMessage({
        id: 'skylab.auiCompanySwitcher.switchCompany',
        defaultMessage: 'Switch company',
      }),
      companyCodeLabel: intl.formatMessage({
        id: 'skylab.auiCompanySwitcher.companyCodeLabel',
        defaultMessage: 'Company code: ',
      }),
      companyLogo: intl.formatMessage({
        id: 'skylab.auiCompanySwitcher.ariaLabel.companyLogo',
        defaultMessage: 'Company logo',
      }),
      currentCompany: intl.formatMessage({
        id: 'skylab.auiCompanySwitcher.currentCompany',
        defaultMessage: 'Current company',
      }),
      searchByCompany: intl.formatMessage({
        id: 'skylab.auiCompanySwitcher.searchByCompany',
        defaultMessage: 'Search by company name or code',
      }),
      parent: intl.formatMessage({
        id: 'skylab.auiCompanySwitcher.parent',
        defaultMessage: 'Parent: ',
      }),
      loadMore: intl.formatMessage({
        id: 'skylab.auiCompanySwitcher.loadMore',
        defaultMessage: 'Load more companies',
      }),
      loading: intl.formatMessage({
        id: 'skylab.auiCompanySwitcher.loading',
        defaultMessage: 'Loading companies...',
      }),
      noResults: intl.formatMessage({
        id: 'skylab.auiCompanySwitcher.noResults',
        defaultMessage: 'No companies found',
      }),
      inactive: intl.formatMessage({
        id: 'skylab.auiCompanySwitcher.inactive',
        defaultMessage: 'Inactive',
      }),
      inSetup: intl.formatMessage({
        id: 'skylab.auiCompanySwitcher.inSetup',
        defaultMessage: 'In setup',
      }),
      manageCompanies: intl.formatMessage({
        id: 'skylab.auiCompanySwitcher.manageCompanies',
        defaultMessage: 'Manage companies',
      }),
    };
  }

  connectedCallback() {
    this.#getIntlObj();
  }

  componentDidRender() {
    this.#createFlyout();
  }

  componentWillUpdate() {
    this.#getIntlObj();
  }

  disconnectedCallback() {
    if (this.#sPopperRef) {
      this.#popperInstanceRef?.destroy();
      this.#sPopperRef.remove();
    }
  }

  #getIntlObj() {
    state.intl
      .then(value => {
        this.intl = value as IntlShape;
      })
      .catch(err => {
        console.error('Error creating intl object:', err);
      });
  }

  #createFlyout() {
    const companySwitcherListDiv = shadowRootParentOrDocument(this.element).getElementById('company-switcher-div');
    if (!companySwitcherListDiv) {
      const div = document.createElement('div');
      div.id = 'company-switcher-div';
      // Keep the popper within the sidenav's stacking context instead of moving to document.body
      const sidenav = this.element.closest('aui-sidenav');
      if (sidenav) {
        sidenav.appendChild(div);
      } else {
        document.body.appendChild(div);
      }
    }
    const popperEl = shadowRootParentOrDocument(this.element).querySelector('s-popper.flyout');
    if (popperEl) {
      const focusedElement = document.activeElement as HTMLElement;
      companySwitcherListDiv.appendChild(popperEl);
      focusedElement?.focus();

      this.#sPopperRef = popperEl as HTMLSPopperElement;
      this.#popperInstanceRef = createPopper(this.element.querySelector('button.company-info'), this.#sPopperRef, {
        placement: 'right-start',
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, this.expanded ? 7 : 3], // triggers are different widths in expanded and collapsed states
            },
          },
        ],
      });
    }
  }

  #showPopper = () => {
    if (this.#sPopperRef) {
      this.#sPopperRef.classList.remove('hidden');
      this.#popperInstanceRef?.update();
      this.showFlyout = true;
      this.#moveFocus();
    }
  };

  #hidePopper = () => {
    if (this.#sPopperRef) {
      this.#sPopperRef.classList.add('hidden');
      this.#popperInstanceRef?.update();
      this.showFlyout = false;
      this.#moveFocus();
    }
  };

  #moveFocus = () => {
    // setTimeout is needed because PopperJs moves the popper div later,
    // then we need to focus to it.
    setTimeout(() => {
      if (this.showFlyout) {
        (this.#sPopperRef.querySelector('button:not([disabled])') as HTMLElement)?.focus();
      } else {
        (this.element.querySelector('button.company-info') as HTMLElement).focus();
      }
    }, 0);
  };

  #handlePopperToggle = () => {
    if (this.showFlyout) {
      this.#hidePopper();
    } else {
      this.#showPopper();
    }
  };

  render() {
    if (!this.intl) return null;
    if (!this.company) {
      if (this.initialCompanyFetchInProgress) {
        return (
          <div class="flex justify-content-center align-items-center">
            <s-loader loading={true} class="margin-top-lg">
              <span slot="loading-message">{this.textLocalized.loading}</span>
            </s-loader>
          </div>
        );
      }
      return null;
    }

    const companyName = this.company?.name || '';
    const companyCode = this.company?.companyCode || '';
    const parentCompany = this.company?.parentCompanyName || '';

    return (
      <Host>
        <div
          class={{
            'company-switcher': true,
            'expanded': this.expanded,
          }}
        >
          <button
            class={{
              'ghost': true,
              'company-info': true,
              'pad-all-md': this.expanded,
            }}
            onClick={this.#handlePopperToggle}
            disabled={!this.mayExpand}
            aria-disabled={(!this.mayExpand).toString()}
          >
            <div class="flex align-items-center justify-content-center">
              {/* Company Icon */}

              {this.renderCompanyIcon()}
              {this.expanded && (
                <div class="company-name-wrapper">
                  <div class="company-name">{companyName}</div>
                  <div class="company-code">{companyCode}</div>
                  {/* Clicking this link toggles the flyout */}
                  {this.mayExpand && <div class="company-link">{this.textLocalized.switchCompany}</div>}
                </div>
              )}
            </div>
          </button>
          {/* Flyout */}

          <s-popper class={{ flyout: true, hidden: !this.showFlyout }}>
            <div class="flyout-header">
              <span class="text-lg">{this.textLocalized.switchCompany}</span>
              <button class="ghost close-button" onClick={this.#hidePopper}>
                <s-icon aria-hidden="true" role="img" name="close"></s-icon>
              </button>
            </div>
            {/* Current company at the top */}
            <button class="ghost company-info" onClick={this.#hidePopper}>
              <div class="flex align-items-center justify-content-center full-width">
                {this.renderCompanyIcon()}
                <div class="company-name-wrapper">
                  <span class="company-name-bold">
                    {companyName} {this.#renderStatusTag(this.company)}
                  </span>
                  <span class="company-code">
                    {this.textLocalized.companyCodeLabel}
                    {companyCode}
                  </span>
                  {parentCompany && (
                    <span class="company-code">
                      {this.textLocalized.parent}
                      {parentCompany}
                    </span>
                  )}
                </div>
              </div>
            </button>
            {/* Search container moved below current company */}
            <div class="search-container">
              <label htmlFor="company-search">{this.textLocalized.searchByCompany}</label>
              <s-input-extended
                type="search-live"
                inputId="company-search"
                placeholder=" "
                value={this.searchText}
                onS-input={this.#handleSelectInput}
                onS-clear={this.#handleSelectClear}
                class="company-search"
              />
            </div>
            <div class="manage-companies-link">
              <a href={`${getAvaAppUrl(window.aui.env)}/company-management/v2/companies`}>{this.textLocalized.manageCompanies}</a>
            </div>
            {/* Other companies */}
            <ul class="company-list">
              {/* Show loader when searching/loading */}
              {this.loading && (
                <li class="loading-indicator">
                  <s-loader loading={true}>
                    <span slot="loading-message">{this.textLocalized.loading}</span>
                  </s-loader>
                </li>
              )}

              {/* Show no results message when not loading and no companies found */}
              {this.companies.length === 0 && !this.loading && <li class="no-results">{this.textLocalized.noResults}</li>}

              {this.companies.map(comp => (
                <li key={comp.id.toString()} onClick={() => this.#handleCompanyClick(comp)}>
                  <button
                    class={{
                      'ghost': true,
                      'company-info': true,
                      'current-company': comp.id === this.company?.id,
                    }}
                  >
                    <div class="company-name">
                      {comp.name} {this.#renderStatusTag(comp)}
                    </div>
                    <div class="company-code">
                      {this.textLocalized.companyCodeLabel}
                      {comp.companyCode || ''}
                    </div>
                    {comp.parentCompanyName && (
                      <div class="company-code">
                        {this.textLocalized.parent} {comp.parentCompanyName}
                      </div>
                    )}
                  </button>
                </li>
              ))}

              {this.hasMore && (
                <li class="load-more-container">
                  {this.loading ? (
                    <s-loader loading={true}>
                      <span slot="loading-message">{this.textLocalized.loading}</span>
                    </s-loader>
                  ) : (
                    <button class="ghost load-more-button" onClick={this.#handleLoadMore}>
                      {this.textLocalized.loadMore}
                    </button>
                  )}
                </li>
              )}
            </ul>
          </s-popper>
        </div>
        {this.expanded && <hr></hr>}
      </Host>
    );
  }
}
